import { isString, isNumber, isObjectLike, every, map } from 'lodash';

class ValidationService {
    constructor() {
        this.EMAIL_REGEX = /^[^\s@]+@[^\s@]{2,}\.[^\s@]{2,}$/;
        this.PHONE_NUMBER_REGEX = /^(\+1)?[0-9]{10}$/;
        this.POSTAL_CODE_REGEX = /^[A-Za-z][0-9][A-Za-z][0-9][A-Za-z][0-9]$/;
        this.ZIP_CODE_REGEX = /^[0-9]{5}(?:-[0-9]{4})?$/;
        this.CREDIT_CARD_NUMBER_REGEX = /^([0-9\s]{15,16})$/;
        this.CREDIT_CARD_CVC_REGEX = /^[0-9]{3,4}$/;
        this.YEAR_REGEX = /^[0-9]{4}$/;
        this.ODOMETER_REGEX = /^[0-9]{1,7}$/;
        this.VIN_REGEX = /^([A-z0-9]{17})?$/;
        this.VIN_RANGE_REGEX = /^([A-z0-9]{1,17})?$/;

        this.validateCardNumber = this.validateCardNumber.bind(this);
        this.validateCardValidationCode = this.validateCardValidationCode.bind(this);
        this.validateEmail = this.validateEmail.bind(this);
        this.validatePassword = this.validatePassword.bind(this);
        this.validateOdometer = this.validateOdometer.bind(this);
        this.validatePhoneNumber = this.validatePhoneNumber.bind(this);
        this.validateVin = this.validateVin.bind(this);
        this.validateYear = this.validateYear.bind(this);
        this.validateZipCode = this.validateZipCode.bind(this);
    }

    optional(validator) {
        return (value) => !value || validator(value);
    }

    getVinRangeRegex() {
        return this.VIN_RANGE_REGEX;
    }

    validateCardNumber(value) {
        return this.validateIsNotEmpty(value) && isString(value) && this.CREDIT_CARD_NUMBER_REGEX.test(value);
    }

    validateCardValidationCode(value) {
        return this.validateIsNotEmpty(value) && isString(value) && this.CREDIT_CARD_CVC_REGEX.test(value);
    }

    validateEmail(value) {
        return isString(value) ? this.EMAIL_REGEX.test(value) : false;
    }

    validateIsNotEmpty(value) {
        if (isString(value)) {
            return value.trim().length !== 0;
        }
        if (isNumber(value)) {
            return isFinite(value); // eslint-disable-line no-restricted-globals
        }
        return !!value;
    }

    validatePassword(value) {
        return this.validateIsNotEmpty(value) && isString(value) && value.length >= 8;
    }

    validateOdometer(value) {
        return this.validateIsNotEmpty(value) && this.ODOMETER_REGEX.test(value);
    }

    validatePhoneNumber(value) {
        return this.validateIsNotEmpty(value) && isString(value) && this.PHONE_NUMBER_REGEX.test(value);
    }

    validateVin(value) {
        return this.VIN_REGEX.test(value);
    }

    validateYear(value) {
        return this.validateIsNotEmpty(value) && this.YEAR_REGEX.test(value);
    }

    validateZipCode(value, country = 'ca') {
        if (country === 'us') {
            return isString(value) ? this.ZIP_CODE_REGEX.test(value.replace(' ', '')) : false;
        }
        return isString(value) ? this.POSTAL_CODE_REGEX.test(value.replace(' ', '')) : false;
    }

    /**
     * @desc Validate if a form is valid. To do this check, this function takes in entry an
     * object which contains the form input name with a boolean value. i.e:
     * ```
     * {
     *    email: true,
     *    name: false,
     *    ....
     * }
     * ```
     * or an array of booleans. ie:
     * ```
     * [true, false]
     * ```
     * and then compares all the value to see if they are all true.
     *
     * An empty object (or array) is considered false.
     *
     * Note: This function does not validate the value of an input. It is the job of the main
     * component to apply the first level validation.
     *
     * @param {Object.<String, Boolean>} formInputValidation an object where the keys are the form
     * inputs name and their validation value. The value must be a Boolean otherwise it will always
     * return false.
     *
     * return {Boolean} true if the form is valid, false otherwise.
     */
    validateForm(formInputValidation) {
        if (!isObjectLike(formInputValidation)) {
            return false;
        }

        function mapHandler(value) {
            return value;
        }

        function everyHandler(value) {
            return value === true;
        }

        const collection = map(formInputValidation, mapHandler);

        // we have to manually check for an empty array
        // because 'every' consider [] to be true

        return collection.length > 0 && every(collection, everyHandler);
    }
}

export default new ValidationService();
